/**
 * Phase 631 — Super Admin Agency CRM (fully autonomous).
 *
 * Observational funnel dashboard over the 1,000+ B2B leads Team 1's
 * SDR Scraper Agent harvests daily. Zero manual send / schedule /
 * bulk-select affordances — every stage transition happens automatically:
 *
 *   NEW      ── Team 1 (SDR Scraper) cron @ 01:00 IST
 *   CONTACTED ── Team 2 (AE Outreach) cron @ 09:45 IST
 *   ENGAGED  ── Phase 151 landing-page telemetry (click + heartbeat)
 *   ON TRIAL ── Phase 631 reconciler joins leads → academies by email
 *   WON      ── Phase 631 reconciler flips on paid Razorpay subscription
 *   LOST     ── Ghost-buster + opt-out sweeps (existing)
 *
 * The only human affordance is a single "Trigger AE outreach now"
 * escape hatch that hand-fires the 09:45 cron off-schedule. No per-lead
 * sends. No bulk selection. No scheduling picker.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '@/lib/api';
import { apiErrorToast } from '@/lib/apiErrorToast';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Search, Phone, Mail, LayoutGrid, List,
  Send, MessageSquare, Clock, ArrowRightLeft,
  RefreshCw, Building2, Users, MapPin, Globe,
  TrendingUp, TrendingDown, Minus, Flame, Zap, Activity,
  Sparkles, BadgeCheck, Archive, Eye, Save, Youtube,
  X, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';

const STAGE_META = {
  new:       { label: 'New',       pillBg: 'bg-slate-100',   pillText: 'text-slate-700',   dot: 'bg-slate-400',   ring: 'border-slate-200' },
  contacted: { label: 'Contacted', pillBg: 'bg-blue-100',    pillText: 'text-blue-700',    dot: 'bg-blue-500',    ring: 'border-blue-200' },
  engaged:   { label: 'Engaged',   pillBg: 'bg-amber-100',   pillText: 'text-amber-700',   dot: 'bg-amber-500',   ring: 'border-amber-200' },
  on_trial:  { label: 'On Trial',  pillBg: 'bg-violet-100',  pillText: 'text-violet-700',  dot: 'bg-violet-500',  ring: 'border-violet-200' },
  won:       { label: 'Won',       pillBg: 'bg-emerald-100', pillText: 'text-emerald-700', dot: 'bg-emerald-500', ring: 'border-emerald-200' },
  lost:      { label: 'Lost',      pillBg: 'bg-rose-100',    pillText: 'text-rose-700',    dot: 'bg-rose-500',    ring: 'border-rose-200' },
};
const STAGE_ORDER = ['new', 'contacted', 'engaged', 'on_trial', 'won', 'lost'];

const relTime = (iso) => {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  const d = Math.floor(s / 86400);
  return d < 30 ? `${d}d ago` : new Date(iso).toLocaleDateString();
};

const formatMins = (secs) => {
  if (!secs || secs < 60) return `${Math.max(0, secs || 0)}s`;
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return s ? `${m}m ${s}s` : `${m}m`;
};

// ─────────────────────────────────────────────────────────────────────
// Activity timeline (drawer sub-component)
// ─────────────────────────────────────────────────────────────────────

const EVENT_ICON = {
  outbound_dispatch: Send,
  channel_dispatch: Mail,
  inbound_reply: MessageSquare,
  status_transition: ArrowRightLeft,
  crm_stage_change: ArrowRightLeft,
};

function ActivityTimeline({ leadId }) {
  const [events, setEvents] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/super-admin/agency-crm/leads/${leadId}/activity`);
      setEvents(r.data.events || []);
    } catch (err) {
      apiErrorToast(err, 'Failed to load activity');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div className="space-y-2 py-2">
      {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 rounded-md" />)}
    </div>
  );
  if (!events || events.length === 0) return (
    <div className="rounded-md border border-dashed border-slate-200 bg-slate-50 py-10 text-center text-xs text-slate-500"
         data-testid="activity-empty">
      No activity yet — the autonomous AE outreach fires at 09:45 IST daily.
    </div>
  );

  return (
    <div className="space-y-3 py-2" data-testid="activity-timeline">
      {events.map((e, idx) => {
        const Icon = EVENT_ICON[e.kind] || Clock;
        const ok = e.success !== false && (e.wa_sent || e.email_sent || e.success || e.auto_reply_sent || e.kind === 'status_transition' || e.kind === 'crm_stage_change');
        const iconColor = ok ? 'text-emerald-600' : (e.error || (e.errors && e.errors.length) ? 'text-rose-600' : 'text-slate-500');
        return (
          <div key={`${e.at}-${idx}`} className="flex gap-3 rounded-lg border border-slate-200 bg-white p-3"
               data-testid={`activity-event-${e.kind}`}>
            <div className={`mt-0.5 h-8 w-8 flex-shrink-0 rounded-full flex items-center justify-center ${ok ? 'bg-emerald-50' : 'bg-slate-100'}`}>
              <Icon className={`h-4 w-4 ${iconColor}`} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                <span className="font-semibold text-slate-800">
                  {e.kind === 'outbound_dispatch' && 'Outbound pitch sent'}
                  {e.kind === 'channel_dispatch' && `${(e.channel || 'channel').toUpperCase()} · ${e.success ? 'delivered' : 'failed'}`}
                  {e.kind === 'inbound_reply' && 'Prospect replied'}
                  {e.kind === 'status_transition' && `Status → ${e.to_stage}`}
                  {e.kind === 'crm_stage_change' && `Moved to ${e.to_stage}`}
                </span>
                <span className="text-slate-400">·</span>
                <span className="font-mono text-slate-500">{relTime(e.at)}</span>
              </div>
              {e.kind === 'outbound_dispatch' && (
                <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px]">
                  <span className={`rounded-full px-2 py-0.5 font-mono ${e.wa_sent ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                    WA {e.wa_sent ? '✓' : (e.wa_attempted ? '✗' : '—')}
                  </span>
                  <span className={`rounded-full px-2 py-0.5 font-mono ${e.email_sent ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                    Email {e.email_sent ? '✓' : (e.email_attempted ? '✗' : '—')}
                  </span>
                  {e.target_name && <span className="text-slate-600">→ {e.target_name}</span>}
                </div>
              )}
              {e.icebreaker_text && (
                <p className="mt-1 text-[11px] italic leading-relaxed text-slate-600 line-clamp-2">
                  &ldquo;{e.icebreaker_text}&rdquo;
                </p>
              )}
              {e.reply_text && (
                <p className="mt-1 text-xs leading-relaxed text-slate-700">
                  &ldquo;{e.reply_text}&rdquo;
                </p>
              )}
              {e.error && (
                <p className="mt-1 text-[11px] text-rose-600 font-mono">{e.error}</p>
              )}
              {e.errors && e.errors.length > 0 && (
                <p className="mt-1 text-[11px] text-rose-600 font-mono line-clamp-2">
                  {e.errors[0]}
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Lead detail drawer — READ-ONLY (Phase 631). No send / send-again.
// ─────────────────────────────────────────────────────────────────────

function LeadDrawer({ lead, open, onClose }) {
  if (!lead) return null;
  const stage = STAGE_META[lead.crm_stage] || STAGE_META.new;
  const bestPhone = (lead.whatsapp_numbers?.[0]) || lead.phone || '';
  const bestEmail = (lead.emails?.[0]) || lead.email || '';

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="lead-drawer">
        <SheetHeader className="pb-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <SheetTitle className="text-lg font-semibold text-slate-900 truncate" data-testid="lead-drawer-title">
                {lead.academy_name}
              </SheetTitle>
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                {lead.city && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{lead.city}</span>}
                {lead.website && (
                  <a href={lead.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-blue-600 hover:underline">
                    <Globe className="h-3 w-3" />{lead.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                  </a>
                )}
              </div>
            </div>
            <span className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-mono uppercase tracking-wider ${stage.pillBg} ${stage.pillText}`}
                  data-testid="lead-drawer-stage-pill">
              {stage.label}
            </span>
          </div>
        </SheetHeader>

        {/* Phase 631 — engagement summary (visible on all leads) */}
        <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-3 grid grid-cols-3 gap-2 text-center"
             data-testid="lead-engagement-summary">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Clicks</div>
            <div className="text-lg font-semibold text-slate-900 mt-0.5" data-testid="lead-click-count">
              {lead.phase150_click_count || 0}
            </div>
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">On Site</div>
            <div className="text-lg font-semibold text-slate-900 mt-0.5" data-testid="lead-site-time">
              {formatMins(lead.phase631_total_seconds_on_site)}
            </div>
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Last visit</div>
            <div className="text-xs font-semibold text-slate-900 mt-1" data-testid="lead-last-visit">
              {lead.phase150_clicked_at ? relTime(lead.phase150_clicked_at) : '—'}
            </div>
          </div>
        </div>

        {lead.trial_started_at && (
          <div className="mb-4 rounded-lg border-2 border-violet-200 bg-violet-50 p-3 flex items-center gap-2"
               data-testid="lead-trial-badge">
            <BadgeCheck className="h-4 w-4 text-violet-700" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-violet-900">Trial active</p>
              <p className="text-[11px] text-violet-700">
                Signed up {relTime(lead.trial_started_at)}
                {lead.trial_academy_id && ` · academy ${lead.trial_academy_id.slice(0, 8)}…`}
              </p>
            </div>
          </div>
        )}

        <Tabs defaultValue="activity">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="activity" data-testid="tab-activity">Activity</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="tab-contacts">Contacts</TabsTrigger>
            <TabsTrigger value="details" data-testid="tab-details">Details</TabsTrigger>
          </TabsList>

          <TabsContent value="activity" className="mt-3">
            <ActivityTimeline leadId={lead.id} />
          </TabsContent>

          <TabsContent value="contacts" className="mt-3 space-y-2">
            <Card><CardContent className="p-3">
              <div className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-2">Primary contact</div>
              {bestPhone && (
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="h-3.5 w-3.5 text-slate-500" />
                  <a href={`tel:${bestPhone}`} className="text-slate-800 hover:underline">{bestPhone}</a>
                </div>
              )}
              {bestEmail && (
                <div className="flex items-center gap-2 text-sm mt-1">
                  <Mail className="h-3.5 w-3.5 text-slate-500" />
                  <a href={`mailto:${bestEmail}`} className="text-slate-800 hover:underline break-all">{bestEmail}</a>
                </div>
              )}
              {!bestPhone && !bestEmail && (
                <p className="text-xs text-slate-500 italic">No contact info on file. Re-run Hunter.io enrichment.</p>
              )}
            </CardContent></Card>

            {(lead.personnel?.length || 0) > 0 && (
              <Card><CardContent className="p-3">
                <div className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1">
                  <Users className="h-3 w-3" />Decision makers (LLM-extracted)
                </div>
                <div className="space-y-1.5">
                  {lead.personnel.map((p, i) => (
                    <div key={i} className="flex items-center justify-between text-xs">
                      <span className="font-semibold text-slate-800">{p.name}</span>
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 font-mono text-[10px] text-slate-600">{p.role}</span>
                    </div>
                  ))}
                </div>
              </CardContent></Card>
            )}
          </TabsContent>

          <TabsContent value="details" className="mt-3 space-y-2">
            <Card><CardContent className="p-3 space-y-1.5 text-xs">
              <DetailRow label="Discovery keyword"  value={lead.keyword} />
              <DetailRow label="Academy scale"      value={lead.academy_scale} />
              <DetailRow label="Raw status"         value={lead.status} mono />
              <DetailRow label="Pipeline stage"     value={lead.pipeline_stage} mono />
              <DetailRow label="Touch #"            value={lead.touch_number} />
              <DetailRow label="Last touched"       value={relTime(lead.last_contacted)} />
              <DetailRow label="Enriched"           value={relTime(lead.phase550a_enriched_at)} />
              {lead.ae_target_name && <DetailRow label="LLM target" value={lead.ae_target_name} />}
              {lead.ae_llm_used !== undefined && <DetailRow label="LLM used" value={lead.ae_llm_used ? 'yes' : 'no'} />}
            </CardContent></Card>
            {lead.ae_icebreaker && (
              <Card><CardContent className="p-3">
                <div className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-2">Latest icebreaker (LLM-generated)</div>
                <p className="text-xs italic text-slate-700 leading-relaxed">&ldquo;{lead.ae_icebreaker}&rdquo;</p>
              </CardContent></Card>
            )}
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}

function DetailRow({ label, value, mono }) {
  if (value === undefined || value === null || value === '') return null;
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-slate-500">{label}</span>
      <span className={`text-slate-800 ${mono ? 'font-mono text-[10px]' : ''}`}>{value}</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Lead card
// ─────────────────────────────────────────────────────────────────────

function LeadCard({ lead, onClick, compact }) {
  const stage = STAGE_META[lead.crm_stage] || STAGE_META.new;
  const bestPhone = (lead.whatsapp_numbers?.[0]) || lead.phone || '';
  const bestEmail = (lead.emails?.[0]) || lead.email || '';
  return (
    <div onClick={onClick}
         className={`cursor-pointer rounded-lg border ${stage.ring} bg-white p-3 hover:shadow-md transition-shadow`}
         data-testid={`lead-card-${lead.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-sm text-slate-900 truncate">{lead.academy_name || 'Unnamed academy'}</p>
          {lead.city && <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
            <MapPin className="h-3 w-3" />{lead.city}
          </p>}
        </div>
        {compact && (
          <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-mono uppercase ${stage.pillBg} ${stage.pillText}`}>
            {stage.label}
          </span>
        )}
      </div>
      <div className="mt-2 flex items-center gap-3 text-[11px] text-slate-500">
        {bestPhone && <span className="flex items-center gap-1 truncate"><Phone className="h-3 w-3" />{bestPhone}</span>}
        {bestEmail && <span className="flex items-center gap-1 truncate"><Mail className="h-3 w-3" />{bestEmail.split('@')[0]}</span>}
      </div>
      <div className="mt-1.5 flex items-center gap-2 text-[10px] text-slate-400">
        {lead.touch_number > 0 && <span>Touch #{lead.touch_number}</span>}
        {lead.last_contacted && <span>· {relTime(lead.last_contacted)}</span>}
        {(lead.phase150_click_count || 0) > 0 && (
          <span className="text-amber-600 font-semibold flex items-center gap-0.5">
            · <Flame className="h-3 w-3" />{lead.phase150_click_count}
          </span>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Insights strip (KPIs + this-week + funnel bar)
// ─────────────────────────────────────────────────────────────────────

function DeltaChip({ current, previous }) {
  const cur = current || 0;
  const prev = previous || 0;
  if (prev === 0 && cur === 0) return null;
  if (prev === 0) return (
    <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-emerald-600">
      <TrendingUp className="h-3 w-3" />new
    </span>
  );
  const delta = cur - prev;
  const pct = Math.round((delta / Math.max(prev, 1)) * 100);
  if (delta === 0) return (
    <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-slate-500">
      <Minus className="h-3 w-3" />0%
    </span>
  );
  const up = delta > 0;
  const Icon = up ? TrendingUp : TrendingDown;
  const cls = up ? 'text-emerald-600' : 'text-rose-600';
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] font-semibold ${cls}`}>
      <Icon className="h-3 w-3" />{up ? '+' : ''}{pct}%
    </span>
  );
}

function KpiTile({ label, value, suffix, testid, accent = 'slate' }) {
  const accentMap = {
    slate: 'text-slate-900',
    blue: 'text-blue-700',
    amber: 'text-amber-700',
    violet: 'text-violet-700',
    emerald: 'text-emerald-700',
    rose: 'text-rose-700',
  };
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid={testid}>
      <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-1 flex items-baseline gap-1">
        <span className={`text-xl font-bold tabular-nums ${accentMap[accent]}`}>{value}</span>
        {suffix && <span className="text-xs text-slate-500">{suffix}</span>}
      </div>
    </div>
  );
}

function InsightsStrip({ insights }) {
  if (!insights) return (
    <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
      {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-lg" />)}
    </div>
  );

  const { kpis, this_week: thisWeek, prev_week: prevWeek, total } = insights;
  return (
    <div className="space-y-3">
      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-7 gap-2" data-testid="kpi-strip">
        <KpiTile label="Total leads"       value={total} testid="kpi-total"       accent="slate" />
        <KpiTile label="Coverage %"        value={insights.coverage?.coverage_pct ?? '—'} suffix={insights.coverage ? '%' : ''} testid="kpi-coverage"    accent="slate" />
        <KpiTile label="Contacted %"       value={kpis.contacted_pct}    suffix="%" testid="kpi-contacted"    accent="blue" />
        <KpiTile label="Engaged %"         value={kpis.engaged_pct}      suffix="%" testid="kpi-engaged"      accent="amber" />
        <KpiTile label="On Trial %"        value={kpis.on_trial_pct}     suffix="%" testid="kpi-on-trial"     accent="violet" />
        <KpiTile label="Won %"             value={kpis.won_pct}          suffix="%" testid="kpi-won"          accent="emerald" />
        <KpiTile label="Trial → Paid"      value={kpis.trial_to_paid_pct} suffix="%" testid="kpi-trial-to-paid" accent="emerald" />
      </div>

      {/* This week strip */}
      <div className="rounded-lg border border-slate-200 bg-gradient-to-r from-slate-50 to-white p-3" data-testid="this-week-strip">
        <div className="flex items-center gap-2 mb-2">
          <Activity className="h-4 w-4 text-slate-600" />
          <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider">This week</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div data-testid="week-new">
            <div className="text-[10px] font-mono uppercase text-slate-500">New leads</div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-lg font-bold text-slate-900 tabular-nums">{thisWeek.new}</span>
              <DeltaChip current={thisWeek.new} previous={prevWeek.new} />
            </div>
          </div>
          <div data-testid="week-engaged">
            <div className="text-[10px] font-mono uppercase text-slate-500">Engaged</div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-lg font-bold text-amber-700 tabular-nums">{thisWeek.engaged}</span>
              <DeltaChip current={thisWeek.engaged} previous={prevWeek.engaged} />
            </div>
          </div>
          <div data-testid="week-trials">
            <div className="text-[10px] font-mono uppercase text-slate-500">Trials started</div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-lg font-bold text-violet-700 tabular-nums">{thisWeek.trials}</span>
              <DeltaChip current={thisWeek.trials} previous={prevWeek.trials} />
            </div>
          </div>
          <div data-testid="week-won">
            <div className="text-[10px] font-mono uppercase text-slate-500">Won</div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-lg font-bold text-emerald-700 tabular-nums">{thisWeek.won}</span>
              <DeltaChip current={thisWeek.won} previous={prevWeek.won} />
            </div>
          </div>
          <div data-testid="week-mrr">
            <div className="text-[10px] font-mono uppercase text-slate-500">MRR added</div>
            <div className="text-lg font-bold text-emerald-700 tabular-nums">
              ₹{(thisWeek.mrr_added_inr || 0).toLocaleString('en-IN')}
            </div>
          </div>
        </div>
      </div>

      {/* Funnel bar */}
      <FunnelBar byStage={insights.by_stage} total={total} />
    </div>
  );
}

function FunnelBar({ byStage, total }) {
  const stages = ['new', 'contacted', 'engaged', 'on_trial', 'won'];
  const widths = stages.map(s => ({
    stage: s,
    count: byStage[s] || 0,
    pct: total ? ((byStage[s] || 0) / total) * 100 : 0,
  }));
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid="funnel-bar">
      <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-2">Funnel · full pipeline</div>
      <div className="flex h-8 rounded-md overflow-hidden">
        {widths.map(({ stage, count, pct }) => {
          const meta = STAGE_META[stage];
          if (pct < 0.5) return null;
          return (
            <div key={stage}
                 style={{ width: `${pct}%` }}
                 className={`${meta.dot} flex items-center justify-center text-[10px] font-mono text-white overflow-hidden`}
                 title={`${meta.label}: ${count}`}
                 data-testid={`funnel-seg-${stage}`}>
              {pct > 6 && `${count}`}
            </div>
          );
        })}
      </div>
      <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-slate-500">
        {stages.map(s => {
          const meta = STAGE_META[s];
          return (
            <span key={s} className="flex items-center gap-1">
              <span className={`h-2 w-2 rounded-full ${meta.dot}`} />
              {meta.label} · {byStage[s] || 0}
            </span>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Hottest engaged leads table
// ─────────────────────────────────────────────────────────────────────

function HottestEngaged({ rows, onOpenLead }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-3" data-testid="hottest-engaged-strip">
      <div className="flex items-center gap-2 mb-2">
        <Flame className="h-4 w-4 text-amber-700" />
        <span className="text-xs font-semibold uppercase tracking-wider text-amber-900">
          Hottest engaged (last 7d)
        </span>
      </div>
      <div className="space-y-1.5">
        {rows.map(r => (
          <button key={r.id}
                  onClick={() => onOpenLead(r.id)}
                  data-testid={`hottest-row-${r.id}`}
                  className="w-full flex items-center justify-between gap-3 rounded-md border border-amber-200 bg-white px-3 py-1.5 text-left hover:shadow-sm transition-shadow">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <span className="font-semibold text-sm text-slate-900 truncate">{r.academy_name || 'Unnamed'}</span>
              {r.city && <span className="text-[11px] text-slate-500">· {r.city}</span>}
            </div>
            <div className="flex items-center gap-4 text-xs">
              <span className="flex items-center gap-1 text-amber-700 font-semibold">
                <Flame className="h-3 w-3" />{r.click_count}
              </span>
              <span className="text-slate-600 tabular-nums">{formatMins(r.seconds_on_site)}</span>
              <span className="text-slate-400">{relTime(r.last_visit_at)}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Coverage Gaps card (Phase 633)
// ─────────────────────────────────────────────────────────────────────

const REASON_LABEL = {
  no_contact_info: 'Missing email + phone',
  opted_out: 'Opted out',
  internal_address: 'Only internal @out-play.in email',
  replied: 'Already replied (closer took over)',
  wa_only_tier_cap: 'WA-only lead · tier cap',
  email_only_pool: 'Email-only lead (queued)',
  stuck_pool: 'Has both channels · untouched',
};

function CoverageGapsCard({ coverage }) {
  const {
    coverage_pct: pct, touched_count: touched,
    enrichable_total: enrichable, untouched_reasons: reasons,
    touched_by_channel: touchedBy, untouched_gt_7d: gt7d,
    ran_at: ranAt,
  } = coverage;
  const isHealthy = pct >= 90;
  const isWarning = pct >= 60 && pct < 90;
  const barColor = isHealthy ? 'bg-emerald-500' : isWarning ? 'bg-amber-500' : 'bg-rose-500';
  const cardColor = isHealthy ? 'border-emerald-200 bg-emerald-50/40' :
                    isWarning ? 'border-amber-200 bg-amber-50/40' :
                    'border-rose-200 bg-rose-50/40';

  return (
    <div className={`rounded-lg border ${cardColor} p-4`} data-testid="coverage-gaps-card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <BadgeCheck className={`h-4 w-4 ${isHealthy ? 'text-emerald-700' : isWarning ? 'text-amber-700' : 'text-rose-700'}`} />
            <span className="text-sm font-semibold text-slate-900">Coverage Guarantee</span>
            <span className="text-[10px] font-mono text-slate-500">
              audited {relTime(ranAt)}
            </span>
          </div>
          <p className="text-xs text-slate-600 mt-0.5">
            <span className="tabular-nums font-bold text-slate-900">{touched}</span> of{' '}
            <span className="tabular-nums font-bold text-slate-900">{enrichable}</span>{' '}
            enrichable leads have received at least one outreach touch.
          </p>
        </div>
        <div className="shrink-0 text-right">
          <div className={`text-2xl font-bold tabular-nums ${isHealthy ? 'text-emerald-700' : isWarning ? 'text-amber-700' : 'text-rose-700'}`}>
            {pct}%
          </div>
          {gt7d > 0 && (
            <div className="text-[10px] font-mono text-rose-600 mt-0.5">
              {gt7d} untouched &gt;7d
            </div>
          )}
        </div>
      </div>
      <div className="h-2 w-full bg-white rounded-full overflow-hidden border border-slate-200 mb-3">
        <div className={`h-full ${barColor} transition-all duration-700`} style={{ width: `${Math.min(100, Math.max(0, pct))}%` }} />
      </div>

      {/* Channel mix on touched leads */}
      {touchedBy && (touchedBy.both + touchedBy.wa_only + touchedBy.email_only) > 0 && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-600 mb-2">
          <span className="flex items-center gap-1">
            <div className="h-2 w-2 rounded-full bg-emerald-500" />
            Both channels: <span className="font-semibold tabular-nums">{touchedBy.both || 0}</span>
          </span>
          <span className="flex items-center gap-1">
            <div className="h-2 w-2 rounded-full bg-blue-500" />
            WhatsApp only: <span className="font-semibold tabular-nums">{touchedBy.wa_only || 0}</span>
          </span>
          <span className="flex items-center gap-1">
            <div className="h-2 w-2 rounded-full bg-violet-500" />
            Email only: <span className="font-semibold tabular-nums">{touchedBy.email_only || 0}</span>
          </span>
        </div>
      )}

      {/* Untouched reasons */}
      {reasons && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-slate-600 border-t border-slate-200 pt-2 mt-1">
          {Object.entries(reasons)
            .filter(([, n]) => n > 0)
            .sort(([, a], [, b]) => b - a)
            .map(([reason, n]) => (
              <div key={reason} className="flex justify-between gap-2"
                   data-testid={`untouched-reason-${reason}`}>
                <span className="text-slate-500">{REASON_LABEL[reason] || reason}</span>
                <span className="font-semibold tabular-nums text-slate-800">{n}</span>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Auto-Heal strip (Phase 634)
// ─────────────────────────────────────────────────────────────────────

function HealingStrip({ healing, onHealNow, triggering }) {
  const {
    ran_at: ranAt, candidates, processed, healed,
    emails_added_total: emailsAdded, phones_added_total: phonesAdded,
    errors_count: errorsCount,
  } = healing;
  const successPct = processed > 0 ? Math.round((healed / processed) * 100) : 0;
  return (
    <div className="rounded-lg border border-teal-200 bg-teal-50/40 p-3"
         data-testid="healing-strip">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Zap className="h-4 w-4 text-teal-700" />
            <span className="text-sm font-semibold text-slate-900">Auto-Heal Sweep</span>
            <span className="text-[10px] font-mono text-slate-500">
              last ran {ranAt ? relTime(ranAt) : 'never'}
            </span>
          </div>
          <p className="text-xs text-slate-600 mt-0.5">
            <span className="tabular-nums font-bold text-slate-900">{healed}</span> of{' '}
            <span className="tabular-nums font-bold text-slate-900">{processed}</span>{' '}
            candidates re-enriched via Hunter.io + website scrape.{' '}
            <span className="text-slate-500">
              +{emailsAdded} emails · +{phonesAdded} phones recovered.
            </span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className={`text-lg font-bold tabular-nums ${successPct >= 30 ? 'text-teal-700' : 'text-slate-600'}`}>
            {successPct}%
          </div>
          <Button size="sm" variant="outline" className="h-7 text-xs"
                  onClick={onHealNow} disabled={triggering}
                  data-testid="heal-now-btn">
            {triggering ? <RefreshCw className="h-3 w-3 mr-1 animate-spin" /> : <Sparkles className="h-3 w-3 mr-1" />}
            Heal now
          </Button>
        </div>
      </div>
      {errorsCount > 0 && (
        <p className="text-[10px] text-rose-600 font-mono">{errorsCount} errors on last run</p>
      )}
      <p className="text-[10px] text-slate-400 mt-1">
        Candidates: {candidates} untouched leads with websites &gt; 7 days old ·
        Cooldown: 3 days between attempts per lead.
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Graceful Archive strip (Phase 635)
// ─────────────────────────────────────────────────────────────────────

function ArchiveStrip({ archive, onArchiveNow, triggering }) {
  const {
    ran_at: ranAt, candidates, archived,
    silence_days_threshold: silenceDays, dry_run: dryRun,
  } = archive;
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/70 p-3"
         data-testid="archive-strip">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Archive className="h-4 w-4 text-slate-600" />
            <span className="text-sm font-semibold text-slate-900">Graceful Archive</span>
            <span className="text-[10px] font-mono text-slate-500">
              last ran {ranAt ? relTime(ranAt) : 'never'}
            </span>
            {dryRun && (
              <span className="text-[9px] font-mono text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-sm">dry-run</span>
            )}
          </div>
          <p className="text-xs text-slate-600 mt-0.5">
            <span className="tabular-nums font-bold text-slate-900">{archived}</span> of{' '}
            <span className="tabular-nums font-bold text-slate-900">{candidates}</span>{' '}
            silent leads moved to <span className="font-semibold">LOST</span>.{' '}
            <span className="text-slate-500">
              Rule: 3+ touches · {silenceDays}d silence · zero engagement.
            </span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" className="h-7 text-xs"
                  onClick={onArchiveNow} disabled={triggering}
                  data-testid="archive-now-btn">
            {triggering ? <RefreshCw className="h-3 w-3 mr-1 animate-spin" /> : <Archive className="h-3 w-3 mr-1" />}
            Archive now
          </Button>
        </div>
      </div>
      <p className="text-[10px] text-slate-400 mt-1">
        Keeps the &ldquo;Contacted&rdquo; column focused on the active funnel — coverage % stays honest.
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────
// Phase 649 — Live Trigger Progress Card
// Polls /turbo-runs/{trigger_id} every 3s while a sweep is in flight and
// renders a live activity feed of just-emailed prospects. Auto-dismisses
// 10s after `finished_at` is set. Persists the active trigger_id in
// localStorage so a page refresh mid-sweep still shows the progress.
// ─────────────────────────────────────────────────────────────────────

const LIVE_TRIGGER_STORAGE_KEY = 'phase649_active_turbo_trigger_id';

function LiveTriggerCard({ triggerId, onDismiss }) {
  const [snapshot, setSnapshot] = useState(null);
  const [error, setError] = useState(null);
  const [autoDismissIn, setAutoDismissIn] = useState(null);
  // Phase 650 — cancel request state
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState(null);

  useEffect(() => {
    if (!triggerId) return undefined;
    let cancelled = false;
    let pollTimer = null;

    const tick = async () => {
      try {
        const r = await api.get(
          `/super-admin/agency-crm/turbo-runs/${triggerId}`,
          { params: { include_recent_emails: 8 } },
        );
        if (cancelled) return;
        setSnapshot(r.data);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        // 404 for the first ~2s is normal (background task still inserting).
        if (err?.response?.status !== 404) {
          setError(err?.response?.data?.detail || 'Poll failed');
        }
      }
    };

    tick();
    pollTimer = setInterval(tick, 3000);
    return () => {
      cancelled = true;
      if (pollTimer) clearInterval(pollTimer);
    };
  }, [triggerId]);

  // Phase 650 — cancel this specific run
  const requestCancel = useCallback(async () => {
    if (!triggerId || cancelling) return;
    setCancelling(true);
    setCancelError(null);
    try {
      await api.post(`/super-admin/agency-crm/turbo-runs/${triggerId}/cancel`);
      toast.success('Cancel request sent. The sweep will stop after the current iteration.');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Cancel failed';
      setCancelError(msg);
      toast.error(msg);
    } finally {
      setCancelling(false);
    }
  }, [triggerId, cancelling]);

  // Auto-dismiss 10s after completion (any terminal state — complete OR cancelled).
  useEffect(() => {
    if (!snapshot?.finished_at) {
      setAutoDismissIn(null);
      return undefined;
    }
    setAutoDismissIn(10);
    const t = setInterval(() => {
      setAutoDismissIn((prev) => {
        if (prev === null) return null;
        if (prev <= 1) {
          onDismiss?.();
          return null;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [snapshot?.finished_at, onDismiss]);

  if (!triggerId) return null;

  const isRunning = snapshot && snapshot.status === 'running';
  const isComplete = snapshot && snapshot.status === 'complete';
  const isCancelled = snapshot && snapshot.status === 'cancelled';
  const cancelPending = snapshot?.cancel_requested_at && isRunning;
  const iterations = snapshot?.iterations || 0;
  const maxIter = snapshot?.max_iterations || snapshot?.iterations || 1;
  const claimed = snapshot?.total_claimed || 0;
  const wa = snapshot?.total_wa_sent || 0;
  const email = snapshot?.total_email_sent || 0;
  const emailOnly = snapshot?.total_email_only_sent || 0;
  const totalReached = wa + email + emailOnly;
  const recent = snapshot?.recent_emails || [];

  // Phase 650 — visual state per terminal status
  const cardTone = isCancelled
    ? 'border-amber-200 bg-amber-50/60'
    : isComplete
      ? 'border-emerald-200 bg-emerald-50/60'
      : cancelPending
        ? 'border-amber-200 bg-amber-50/60'
        : 'border-blue-200 bg-blue-50/60';
  const barColor = isCancelled
    ? 'bg-amber-500'
    : isComplete
      ? 'bg-emerald-500'
      : cancelPending
        ? 'bg-amber-500'
        : 'bg-blue-500';

  return (
    <div
      className={`rounded-xl border p-4 shadow-sm ${cardTone}`}
      data-testid="live-trigger-card"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          {isRunning && !cancelPending && (
            <Zap className="h-4 w-4 text-blue-600 animate-pulse flex-shrink-0" />
          )}
          {isRunning && cancelPending && (
            <RefreshCw className="h-4 w-4 text-amber-600 animate-spin flex-shrink-0" />
          )}
          {isComplete && (
            <BadgeCheck className="h-4 w-4 text-emerald-600 flex-shrink-0" />
          )}
          {isCancelled && (
            <X className="h-4 w-4 text-amber-600 flex-shrink-0" />
          )}
          {!snapshot && (
            <RefreshCw className="h-4 w-4 text-slate-400 animate-spin flex-shrink-0" />
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-slate-900" data-testid="live-trigger-status">
                {isCancelled ? 'Turbo drain cancelled'
                  : isComplete ? 'Turbo drain complete'
                  : cancelPending ? 'Cancelling…'
                  : isRunning ? 'Turbo drain in flight'
                  : 'Starting up…'}
              </span>
              {snapshot?.kind && (
                <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500 border border-slate-200 rounded px-1.5 py-0.5">
                  {snapshot.kind.replace('_', ' ')}
                </span>
              )}
            </div>
            <p className="text-[11px] text-slate-500 mt-0.5">
              trigger_id <span className="font-mono">{triggerId.slice(0, 8)}…</span>
              {snapshot?.started_at && (
                <> · started {relTime(snapshot.started_at)}</>
              )}
              {autoDismissIn !== null && (
                <> · auto-dismissing in {autoDismissIn}s</>
              )}
              {isCancelled && snapshot?.cancel_requested_by && (
                <> · by {snapshot.cancel_requested_by}</>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {/* Phase 650 — Cancel button (only while running + no cancel already requested) */}
          {isRunning && !cancelPending && (
            <Button
              size="sm" variant="outline"
              onClick={requestCancel}
              disabled={cancelling}
              className="h-7 text-xs border-amber-300 text-amber-700 hover:bg-amber-50 hover:border-amber-400 hover:text-amber-800"
              data-testid="live-trigger-cancel-btn"
            >
              {cancelling ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Cancelling…</> : 'Cancel run'}
            </Button>
          )}
          <Button
            size="sm" variant="ghost"
            onClick={onDismiss}
            className="h-7 text-xs text-slate-500 hover:text-slate-900"
            data-testid="live-trigger-dismiss-btn"
          >
            Dismiss
          </Button>
        </div>
      </div>

      {error && (
        <p className="text-[11px] text-rose-600 mb-2">Poll error: {String(error).slice(0, 120)}</p>
      )}
      {cancelError && (
        <p className="text-[11px] text-rose-600 mb-2">{cancelError}</p>
      )}

      {/* Iteration progress bar */}
      {(snapshot?.kind === 'turbo_drain' || iterations > 0) && (
        <div className="mb-3">
          <div className="flex items-center justify-between text-[11px] text-slate-600 mb-1">
            <span>Iterations</span>
            <span className="tabular-nums font-semibold text-slate-900">{iterations} / {maxIter}</span>
          </div>
          <div className="h-1.5 w-full bg-white rounded-full overflow-hidden border border-slate-200">
            <div
              className={`h-full transition-all duration-500 ${barColor}`}
              style={{ width: `${Math.min(100, Math.max(0, (iterations / maxIter) * 100))}%` }}
            />
          </div>
        </div>
      )}

      {/* Counter grid */}
      <div className="grid grid-cols-4 gap-2 mb-3">
        {[
          { label: 'Claimed', value: claimed, color: 'text-slate-900' },
          { label: 'WhatsApp', value: wa, color: 'text-emerald-700' },
          { label: 'Email', value: email + emailOnly, color: 'text-violet-700' },
          { label: 'Total reached', value: totalReached, color: 'text-blue-700' },
        ].map((c) => (
          <div
            key={c.label}
            className="rounded-lg bg-white border border-slate-200 px-2.5 py-2 text-center"
            data-testid={`live-trigger-counter-${c.label.toLowerCase().replace(' ', '-')}`}
          >
            <div className={`text-lg font-bold tabular-nums ${c.color}`}>{c.value}</div>
            <div className="text-[10px] font-medium uppercase tracking-wider text-slate-500">{c.label}</div>
          </div>
        ))}
      </div>

      {/* Live email tail */}
      {recent.length > 0 && (
        <div
          className="rounded-lg bg-white border border-slate-200 max-h-40 overflow-y-auto"
          data-testid="live-trigger-recent-emails"
        >
          <div className="sticky top-0 bg-slate-50 border-b border-slate-200 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500 flex items-center justify-between">
            <span className="flex items-center gap-1.5"><Mail className="h-3 w-3" /> Just delivered</span>
            <span className="tabular-nums">{recent.length}</span>
          </div>
          <ul className="divide-y divide-slate-100">
            {recent.map((e) => (
              <li key={`${e.to}-${e.created_at}`} className="px-3 py-1.5 flex items-center justify-between gap-3 text-[11px]">
                <div className="min-w-0 flex-1 flex items-center gap-2">
                  <div className={`h-1.5 w-1.5 rounded-full flex-shrink-0 ${e.status === 'sent' ? 'bg-emerald-500' : 'bg-rose-400'}`} />
                  <span className="truncate text-slate-700">{e.to}</span>
                </div>
                <span className="text-slate-400 tabular-nums font-mono flex-shrink-0">{relTime(e.created_at)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {isRunning && !cancelPending && recent.length === 0 && (
        <p className="text-[11px] text-slate-500 italic">Warming up LLM icebreakers · first sends usually land within 8-12s.</p>
      )}
      {cancelPending && (
        <p className="text-[11px] text-amber-700 italic">Cancel signal accepted · will stop after the current iteration finishes ({snapshot?.iterations || 0} done).</p>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────

export default function SuperAdminAgencyCRM() {
  const [leads, setLeads] = useState([]);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  const [viewMode, setViewMode] = useState('kanban');
  const [selectedLead, setSelectedLead] = useState(null);
  const [triggering, setTriggering] = useState(false);
  // Phase 647.b — Preview intro email + SA-editable YouTube URL modal
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');
  const [ytUrlInput, setYtUrlInput] = useState('');
  const [ytUrlEffective, setYtUrlEffective] = useState('');
  const [ytSaving, setYtSaving] = useState(false);
  // Phase 649 — Live trigger progress card. Set when the SA clicks
  // "Trigger AE outreach now"; persisted in localStorage so a page
  // refresh mid-sweep still renders the live progress.
  const [activeTriggerId, setActiveTriggerId] = useState(() => {
    try { return localStorage.getItem(LIVE_TRIGGER_STORAGE_KEY) || null; }
    catch (_e) { return null; }
  });
  const dismissActiveTrigger = useCallback(() => {
    setActiveTriggerId(null);
    try { localStorage.removeItem(LIVE_TRIGGER_STORAGE_KEY); } catch (_e) { /* noop */ }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [leadsRes, insightsRes] = await Promise.all([
        api.get('/super-admin/agency-crm/leads', {
          params: {
            search: search || undefined,
            stage: stageFilter !== 'all' ? stageFilter : undefined,
            limit: 300,
          },
        }),
        api.get('/super-admin/agency-crm/funnel-insights'),
      ]);
      setLeads(leadsRes.data.rows || []);
      setInsights(insightsRes.data);
    } catch (err) {
      apiErrorToast(err, 'Failed to load CRM');
    } finally {
      setLoading(false);
    }
  }, [search, stageFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Phase 631 — auto-refresh insights every 60s so the CEO sees real-time
  // stage-flips as the autonomous crons fire. Cheap: single JSON payload.
  useEffect(() => {
    const t = setInterval(async () => {
      try {
        const r = await api.get('/super-admin/agency-crm/funnel-insights');
        setInsights(r.data);
      } catch (_e) { /* silent — will retry next tick */ }
    }, 60_000);
    return () => clearInterval(t);
  }, []);

  const triggerOutreachNow = useCallback(async () => {
    if (!window.confirm(
      'Turbo Drain: keep looping AE Outreach sweeps until every eligible lead is contacted (respects Meta tier caps + academy limits). Uses real WhatsApp + Email credits. Proceed?'
    )) return;
    setTriggering(true);
    try {
      // Phase 646 — Step 1: revive null-status leads so they re-enter the pipeline
      let revivedNote = '';
      try {
        const rev = await api.post('/super-admin/agency-crm/revive-null-status-leads');
        if ((rev?.data?.total_flipped || 0) > 0) {
          revivedNote = ` · ${rev.data.total_flipped} stuck leads revived`;
        }
      } catch (_e) { /* non-blocking */ }

      // Phase 646 — Step 2: fire the turbo-drain sweep
      const r = await api.post(
        '/super-admin/agency-crm/trigger-outreach-now',
        null,
        { params: { daily_cap: 200, turbo: true, max_iterations: 10 } }
      );
      const triggerId = r.data?.trigger_id;
      toast.success(
        `${r.data.message || 'Turbo drain dispatched'}${revivedNote}`,
        { duration: 5000 }
      );

      // Phase 649 — hand the trigger_id to the LiveTriggerCard which
      // polls every 3s and renders live progress. No more blind 40s
      // wait + single completion toast; the card auto-dismisses 10s
      // after `finished_at` is set. Persist to localStorage so a page
      // refresh during the sweep still shows progress.
      if (triggerId) {
        setActiveTriggerId(triggerId);
        try { localStorage.setItem(LIVE_TRIGGER_STORAGE_KEY, triggerId); } catch (_e) { /* noop */ }
      }
      // Also refresh insights in ~15s so counters catch the first contacted lead
      setTimeout(fetchData, 15_000);
    } catch (err) {
      apiErrorToast(err, 'Trigger failed');
    } finally {
      setTriggering(false);
    }
  }, [fetchData]);

  // Phase 647.b — Open the Preview Intro Email modal. Fetches both the
  // current SA-configured YouTube URL AND the rendered HTML in parallel
  // so the modal has everything on first paint (no double-loading state).
  const openPreviewModal = useCallback(async () => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    try {
      const [cfg, prev] = await Promise.all([
        api.get('/super-admin/agency-crm/intro-email-config'),
        api.get('/super-admin/agency-crm/preview-intro-email'),
      ]);
      setYtUrlInput(cfg.data?.youtube_url || '');
      setYtUrlEffective(cfg.data?.effective || '');
      setPreviewHtml(prev.data?.html || '');
      setPreviewSubject(prev.data?.subject || '');
    } catch (err) {
      apiErrorToast(err, 'Failed to load preview');
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  // Phase 647.b — Save the SA-edited YouTube URL then reload the preview.
  const saveYoutubeUrl = useCallback(async () => {
    setYtSaving(true);
    try {
      const save = await api.put('/super-admin/agency-crm/intro-email-config', {
        youtube_url: ytUrlInput.trim(),
      });
      toast.success(save.data?.cleared ? 'Cleared override — using default video' : 'YouTube URL saved');
      // Refresh the rendered HTML so the SA sees the change immediately.
      const prev = await api.get('/super-admin/agency-crm/preview-intro-email');
      setPreviewHtml(prev.data?.html || '');
      setYtUrlEffective(prev.data?.youtube_url_effective || '');
    } catch (err) {
      apiErrorToast(err, 'Save failed');
    } finally {
      setYtSaving(false);
    }
  }, [ytUrlInput]);

  // Phase 634 — one-click auto-heal kicker
  const triggerHealNow = useCallback(async () => {
    if (!window.confirm('Fire the auto-heal sweep now? Runs Hunter.io + website phone-regex scrape on up to 50 untouched leads.')) return;
    setTriggering(true);
    try {
      const r = await api.post('/super-admin/agency-crm/heal-now?cap=50');
      toast.success(r.data.message || 'Auto-heal sweep dispatched', { duration: 6000 });
      setTimeout(fetchData, 15_000);
    } catch (err) {
      apiErrorToast(err, 'Heal trigger failed');
    } finally {
      setTriggering(false);
    }
  }, [fetchData]);

  // Phase 635 — one-click graceful-archive kicker
  const triggerArchiveNow = useCallback(async () => {
    if (!window.confirm('Fire the graceful-archive sweep now? Flips leads with 3+ touches + 15d silence + zero engagement → LOST.')) return;
    setTriggering(true);
    try {
      const r = await api.post('/super-admin/agency-crm/graceful-archive/run?cap=200');
      toast.success(r.data.message || 'Graceful-archive sweep dispatched', { duration: 6000 });
      setTimeout(fetchData, 10_000);
    } catch (err) {
      apiErrorToast(err, 'Archive trigger failed');
    } finally {
      setTriggering(false);
    }
  }, [fetchData]);

  const openLeadById = useCallback(async (leadId) => {
    const found = leads.find(l => l.id === leadId);
    if (found) {
      setSelectedLead(found);
      return;
    }
    // Fallback: fetch the lead detail directly
    try {
      const r = await api.get(`/super-admin/agency-crm/leads/${leadId}`);
      if (r.data) setSelectedLead(r.data);
    } catch (_e) {
      toast.error('Could not load lead');
    }
  }, [leads]);

  const leadsByStage = useMemo(() => {
    const acc = { new: [], contacted: [], engaged: [], on_trial: [], won: [], lost: [] };
    leads.forEach(l => { (acc[l.crm_stage] || acc.new).push(l); });
    return acc;
  }, [leads]);

  if (loading) return (
    <div className="p-6 space-y-4">
      <Skeleton className="h-9 w-64" />
      <div className="grid grid-cols-6 gap-3">{STAGE_ORDER.map(s => <Skeleton key={s} className="h-72 rounded-xl" />)}</div>
    </div>
  );

  return (
    <div className="p-6 space-y-5" data-testid="super-admin-agency-crm-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-slate-500" />
            <h1 className="text-2xl font-semibold text-slate-900">Agency CRM</h1>
            <span className="rounded-full bg-emerald-100 text-emerald-700 px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider flex items-center gap-1">
              <Sparkles className="h-3 w-3" />autonomous
            </span>
          </div>
          <p className="text-xs text-slate-500 mt-0.5">
            Zero-touch funnel · Team 1 scrapes → Team 2 outreach 09:45 IST → prospects self-engage → Phase 631 reconciler stamps trials + wins.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={openPreviewModal} disabled={triggering}
                  className="h-9 text-xs"
                  data-testid="preview-intro-email-btn"
                  title="See the exact rich intro email a prospect will receive + edit the YouTube walkthrough URL">
            <Eye className="h-3.5 w-3.5 mr-1" />
            Preview intro email
          </Button>
          <Button size="sm" onClick={triggerOutreachNow} disabled={triggering}
                  className="h-9 text-xs bg-slate-900 hover:bg-slate-800"
                  data-testid="trigger-outreach-now-btn">
            {triggering ? <RefreshCw className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Zap className="h-3.5 w-3.5 mr-1" />}
            Trigger AE outreach now
          </Button>
          <div className="flex bg-slate-100 rounded-lg p-0.5">
            <Button variant={viewMode === 'kanban' ? 'default' : 'ghost'} size="sm"
                    className="h-7 text-xs rounded-md"
                    onClick={() => setViewMode('kanban')}
                    data-testid="view-kanban-btn">
              <LayoutGrid className="h-3.5 w-3.5 mr-1" />Kanban
            </Button>
            <Button variant={viewMode === 'list' ? 'default' : 'ghost'} size="sm"
                    className="h-7 text-xs rounded-md"
                    onClick={() => setViewMode('list')}
                    data-testid="view-list-btn">
              <List className="h-3.5 w-3.5 mr-1" />List
            </Button>
          </div>
          <Button size="sm" variant="outline" onClick={fetchData} className="h-9"
                  data-testid="refresh-btn">
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Phase 649 — Live trigger progress card */}
      {activeTriggerId && (
        <LiveTriggerCard triggerId={activeTriggerId} onDismiss={dismissActiveTrigger} />
      )}

      {/* Insights strip (KPIs + this-week + funnel bar) */}
      <InsightsStrip insights={insights} />

      {/* Hottest engaged (top 5 by click_count in last 7d) */}
      {insights && (
        <HottestEngaged rows={insights.hottest_engaged} onOpenLead={openLeadById} />
      )}

      {/* Phase 633 — Coverage gaps card */}
      {insights?.coverage && (
        <CoverageGapsCard coverage={insights.coverage} />
      )}

      {/* Phase 634 — Auto-heal strip */}
      {insights?.healing && (
        <HealingStrip healing={insights.healing} onHealNow={triggerHealNow} triggering={triggering} />
      )}

      {/* Phase 635 — Graceful archive strip */}
      {insights?.archive && (
        <ArchiveStrip archive={insights.archive} onArchiveNow={triggerArchiveNow} triggering={triggering} />
      )}

      {/* Stage filter pills */}
      {insights && (
        <div className="flex flex-wrap gap-2" data-testid="stage-filter-strip">
          <Button size="sm" variant={stageFilter === 'all' ? 'default' : 'outline'}
                  className="h-7 text-xs"
                  onClick={() => setStageFilter('all')}
                  data-testid="stage-filter-all">
            All ({insights.total})
          </Button>
          {STAGE_ORDER.map(s => {
            const meta = STAGE_META[s];
            const count = insights.by_stage?.[s] || 0;
            return (
              <Button key={s} size="sm"
                      variant={stageFilter === s ? 'default' : 'outline'}
                      className="h-7 text-xs"
                      onClick={() => setStageFilter(stageFilter === s ? 'all' : s)}
                      data-testid={`stage-filter-${s}`}>
                <div className={`h-2 w-2 rounded-full ${meta.dot} mr-1.5`} />
                {meta.label} ({count})
              </Button>
            );
          })}
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)}
               placeholder="Search academy, city, email, phone..."
               className="pl-9 h-9"
               data-testid="crm-search-input" />
      </div>

      {/* KANBAN */}
      {viewMode === 'kanban' && (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="kanban-board">
          {STAGE_ORDER.map(s => {
            const meta = STAGE_META[s];
            const cardsAtStage = leadsByStage[s] || [];
            return (
              <div key={s} className="space-y-2" data-testid={`kanban-col-${s}`}>
                <div className="flex items-center justify-between px-1">
                  <div className="flex items-center gap-2">
                    <div className={`h-3 w-3 rounded-full ${meta.dot}`} />
                    <span className="text-sm font-semibold text-slate-800">{meta.label}</span>
                  </div>
                  <Badge variant="secondary" className="text-[10px] h-5 tabular-nums">
                    {cardsAtStage.length}
                  </Badge>
                </div>
                <div className="space-y-2 min-h-[300px] p-2 rounded-xl bg-slate-50/60 border border-dashed border-slate-200">
                  {cardsAtStage.length === 0 ? (
                    <p className="text-xs text-slate-400 text-center py-10">Empty</p>
                  ) : cardsAtStage.slice(0, 30).map(lead => (
                    <LeadCard key={lead.id} lead={lead} onClick={() => setSelectedLead(lead)} />
                  ))}
                  {cardsAtStage.length > 30 && (
                    <p className="text-[10px] text-slate-400 text-center pt-2">
                      +{cardsAtStage.length - 30} more · use List view
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* LIST */}
      {viewMode === 'list' && (
        <div className="space-y-2" data-testid="list-view">
          {leads.length === 0 ? (
            <Card className="border-dashed"><CardContent className="flex flex-col items-center justify-center py-16">
              <Building2 className="h-12 w-12 text-slate-300 mb-4" />
              <h3 className="text-lg font-semibold mb-1">No leads match your filter</h3>
              <p className="text-sm text-slate-500">Clear the search or stage filter to see more.</p>
            </CardContent></Card>
          ) : (
            <>
              {leads.slice(0, 200).map(lead => (
                <LeadCard key={lead.id} lead={lead} onClick={() => setSelectedLead(lead)} compact />
              ))}
              {leads.length > 200 && (
                <p className="text-xs text-slate-500 text-center py-4" data-testid="list-truncation-hint">
                  Showing first 200 of {leads.length} — refine search or use Kanban view to see more.
                </p>
              )}
            </>
          )}
        </div>
      )}

      <LeadDrawer
        lead={selectedLead}
        open={!!selectedLead}
        onClose={() => setSelectedLead(null)}
      />

      {/* Phase 647.b — Preview Intro Email + SA-editable YouTube URL */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent
          className="max-w-4xl max-h-[92vh] overflow-hidden flex flex-col p-0"
          data-testid="preview-intro-email-dialog"
        >
          <DialogHeader className="p-5 pb-3 border-b">
            <DialogTitle className="flex items-center gap-2 text-base">
              <Eye className="h-4 w-4 text-slate-500" />
              Preview intro email
            </DialogTitle>
            <DialogDescription className="text-xs">
              Exact rich Touch-1 email a prospect will receive when the AE Outreach sweep fires.
              Edit the YouTube walkthrough URL below — saves live, applies from the next sweep onward.
            </DialogDescription>
          </DialogHeader>

          {/* YouTube URL editor row */}
          <div className="px-5 py-3 border-b bg-slate-50/50 space-y-2">
            <div className="flex items-center gap-2 text-xs text-slate-600">
              <Youtube className="h-4 w-4 text-rose-500" />
              <span className="font-semibold">YouTube walkthrough URL</span>
              <span className="text-slate-400 truncate max-w-[240px]" title={ytUrlEffective}>
                (effective: {ytUrlEffective || '—'})
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Input
                value={ytUrlInput}
                onChange={(e) => setYtUrlInput(e.target.value)}
                placeholder="https://youtu.be/… or leave blank to use default"
                className="h-8 font-mono text-xs"
                data-testid="preview-intro-email-yt-input"
              />
              <Button
                size="sm" onClick={saveYoutubeUrl} disabled={ytSaving}
                className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="preview-intro-email-yt-save-btn"
              >
                {ytSaving ? <RefreshCw className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}
                Save
              </Button>
            </div>
          </div>

          {/* Rendered subject line + iframe with the email HTML */}
          <div className="flex-1 overflow-auto">
            {previewLoading ? (
              <div className="p-8 flex items-center justify-center text-slate-500 text-sm">
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Loading preview…
              </div>
            ) : (
              <div className="p-5 space-y-3">
                <div className="text-xs text-slate-500">
                  <span className="font-semibold text-slate-700">Subject:</span> {previewSubject || '—'}
                </div>
                <iframe
                  title="Intro email preview"
                  srcDoc={`<html><body style="margin:0;background:#f8fafc;padding:20px;">${previewHtml}</body></html>`}
                  className="w-full h-[520px] border border-slate-200 rounded-lg bg-white"
                  sandbox=""
                  data-testid="preview-intro-email-iframe"
                />
              </div>
            )}
          </div>

          <DialogFooter className="p-4 border-t bg-slate-50/50">
            <Button
              variant="outline" size="sm" onClick={() => setPreviewOpen(false)}
              data-testid="preview-intro-email-close-btn"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
